import { Column, Container, Row } from '@cdab/scania/qpr/components/atoms'
import {
  useCssVariableBreakpoint,
  useGetAreaReport,
  useGetMarketDeviationsReport,
  useGetMarketSummaryReport
} from '@cdab/scania/qpr/hooks'
import type { Area, Market } from '@cdab/scania/qpr/schema'
import { capitalizeFirstLetter } from '@cdab/utils'
import {
  TdsButton,
  TdsDatetime,
  TdsDropdown,
  TdsDropdownOption,
  TdsMessage
} from '@scania/tegel-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FullWidth,
  MessagesRow,
  StyledFirstRow,
  StyledMessage
} from './area-market-report.styles'
import type { AreaMarketReportProps } from './area-market-report.types'

export function AreaMarketReport({
  reportType,
  areas,
  markets = []
}: AreaMarketReportProps) {
  const { t } = useTranslation(['reports', 'common'])
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')

  const [marketsToShowState, setMarketsToShowState] = useState<Market[]>([])

  const [marketIdsState, setMarketIdsState] = useState<number[]>([])
  const [areaIdsState, setAreaIdsState] = useState<number[]>([])

  const [dateFromState, setDateFromState] = useState<string>('')
  const [dateToState, setDateToState] = useState<string>('')

  const [getAreaReportState, clickGetAreaReportState] =
    useGetAreaReport(areaIdsState)

  const areaReportDisbled =
    getAreaReportState.status === 'loading' || areaIdsState.length === 0

  const [getMarketSummaryReportState, clickGetMarketSummaryReportState] =
    useGetMarketSummaryReport(marketIdsState, areaIdsState)

  const marketSummaryReportDisbled =
    getMarketSummaryReportState.status === 'loading' ||
    areaIdsState.length === 0

  const [getMarketDeviationsReportState, clickGetMarketDeviationsReportState] =
    useGetMarketDeviationsReport(marketIdsState, dateFromState, dateToState)

  const marketDeviationsReportDisbled =
    getMarketDeviationsReportState.status === 'loading' ||
    marketIdsState.length === 0 ||
    dateFromState === '' ||
    dateToState === ''

  const onChangeAreas = useCallback(
    (value: string | null) => {
      if (value === null) return
      const selectedAreaIds = value
        .split(',')
        .filter(id => id.trim() !== '')
        .map(id => parseInt(id.trim()))

      setAreaIdsState(selectedAreaIds)
      setMarketIdsState(selectedAreaIds.length === 1 ? marketIdsState : [])
      setMarketsToShowState(
        selectedAreaIds.length === 1
          ? markets.filter(market => market.areaId === selectedAreaIds[0])
          : []
      )
    },
    [marketIdsState, markets]
  )

  const onChangeMarkets = useCallback((value: string | null) => {
    if (value === null) return
    const selectedMarketIds = value
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim()))

    setMarketIdsState(selectedMarketIds)
  }, [])

  return (
    <Container fluid='normal' fullHeight>
      <StyledFirstRow>
        <Column width={12} lg={8} offset={{ lg: 2 }}>
          <Row>
            <Column width={12} lg={reportType === 'area-summary' ? 12 : 6}>
              <FullWidth>
                <TdsDropdown
                  modeVariant='secondary'
                  id={reportType + '-areas'}
                  label={capitalizeFirstLetter(t('areas', { ns: 'common' }))}
                  size='lg'
                  disabled={false}
                  labelPosition='outside'
                  placeholder={capitalizeFirstLetter(
                    t('areas', { ns: 'common' })
                  )}
                  multiselect
                  onTdsChange={e => onChangeAreas(e.detail.value)}
                >
                  {areas
                    .sort((a: Area, b: Area) => a.name.localeCompare(b.name))
                    .map(({ id, name }) => {
                      return (
                        <TdsDropdownOption
                          key={'areas' + id.toString()}
                          value={id.toString()}
                        >
                          {name}
                        </TdsDropdownOption>
                      )
                    })}
                </TdsDropdown>
              </FullWidth>
            </Column>
            <Column width={12} lg={6}>
              {reportType !== 'area-summary' &&
                marketsToShowState.length > 0 && (
                  <FullWidth>
                    <TdsDropdown
                      modeVariant='secondary'
                      id={reportType + '-markets'}
                      label={capitalizeFirstLetter(
                        t('markets', { ns: 'common' })
                      )}
                      size='lg'
                      disabled={false}
                      labelPosition='outside'
                      placeholder={capitalizeFirstLetter(
                        t('markets', { ns: 'common' })
                      )}
                      multiselect
                      onTdsChange={e => onChangeMarkets(e.detail.value)}
                    >
                      {marketsToShowState
                        .sort((a: Market, b: Market) =>
                          a.marketName.localeCompare(b.marketName)
                        )
                        .map(({ id, marketName }) => {
                          return (
                            <TdsDropdownOption
                              key={'markets' + id.toString()}
                              value={id.toString()}
                            >
                              {marketName}
                            </TdsDropdownOption>
                          )
                        })}
                    </TdsDropdown>
                  </FullWidth>
                )}
            </Column>
          </Row>

          {reportType === 'top-market-deviations' && (
            <Row className='tds-u-mt2'>
              <Column width={12} lg={6} padding={isLg}>
                <TdsDatetime
                  name='dateFrom'
                  modeVariant='secondary'
                  noMinWidth={true}
                  label={t('date-from', { ns: 'common' })}
                  onTdsInput={e => setDateFromState(e.target.value)}
                  type='date'
                />
              </Column>
              <Column width={12} lg={6} padding={isLg}>
                <TdsDatetime
                  name='dateTo'
                  modeVariant='secondary'
                  noMinWidth={true}
                  label={t('date-to', { ns: 'common' })}
                  onTdsInput={e => setDateToState(e.target.value)}
                  type='date'
                />
              </Column>
            </Row>
          )}

          <MessagesRow>
            {reportType === 'area-summary' &&
              getAreaReportState.status === 'error' && (
                <StyledMessage className='tds-u-mt2'>
                  <TdsMessage
                    modeVariant='secondary'
                    header={`${capitalizeFirstLetter(t('area-summary'))}: ${
                      getAreaReportState.message
                    } `}
                    variant={'error'}
                  />
                </StyledMessage>
              )}
            {reportType === 'country-summary' &&
              getMarketSummaryReportState.status === 'error' && (
                <StyledMessage className='tds-u-mt2'>
                  <TdsMessage
                    modeVariant='secondary'
                    header={`${capitalizeFirstLetter(t('country-summary'))}: ${
                      getMarketSummaryReportState.message
                    } `}
                    variant={'error'}
                  />
                </StyledMessage>
              )}
            {reportType === 'top-market-deviations' &&
              getMarketDeviationsReportState.status === 'error' && (
                <StyledMessage className='tds-u-mt2'>
                  <TdsMessage
                    modeVariant='secondary'
                    header={`${t('top-market-deviations')}: ${
                      getMarketDeviationsReportState.message
                    } `}
                    variant={'error'}
                  />
                </StyledMessage>
              )}
          </MessagesRow>
          <Row className='tds-u-mt2'>
            {reportType === 'area-summary' ? (
              <TdsButton
                variant='primary'
                size='md'
                text={capitalizeFirstLetter(t('area-summary'))}
                onClick={() => areaReportDisbled || clickGetAreaReportState()}
                disabled={areaReportDisbled}
              />
            ) : reportType === 'country-summary' ? (
              <TdsButton
                variant='primary'
                size='md'
                text={capitalizeFirstLetter(t('country-summary'))}
                onClick={() =>
                  marketSummaryReportDisbled ||
                  clickGetMarketSummaryReportState()
                }
                disabled={marketSummaryReportDisbled}
              />
            ) : (
              reportType === 'top-market-deviations' && (
                <TdsButton
                  variant='primary'
                  size='md'
                  text={t('top-market-deviations')}
                  onClick={() =>
                    marketDeviationsReportDisbled ||
                    clickGetMarketDeviationsReportState()
                  }
                  disabled={marketDeviationsReportDisbled}
                />
              )
            )}
          </Row>
        </Column>
      </StyledFirstRow>
    </Container>
  )
}
